import * as React from "react";
import SideNavigation from "@amzn/awsui-components-react/polaris/side-navigation";
import Header from "@amzn/awsui-components-react/polaris/header";
import Box from "@amzn/awsui-components-react/polaris/box";
import PageShell from "../../../../global/components/PageShell";
import {HashPageRouter} from "../../../utils/hashPageRouter";
import AddPrescriptionV2 from "./prescription/components/add_prescription/AddPrescriptionV2";
import AddPrescriptionNewRx from "./prescription/components/add_prescription_newrx/AddPrescriptionNewRx";
import InsurancePlanSection from "./insurance/components/add_insurance/InsurancePlanSection";
import UpdateInsuranceSection from "./update-insurance/components/update_insurance/UpdateInsuranceSection";
import CreateSubscriptionSection from "./subscription/components/create_subscription/CreateSubscriptionSection";
import UpdateIdentityValidationStatusSection
  from "./identity/components/update_identity_validation_status/UpdateIdentityValidationStatusSection";
import OrderSection from "./order/components/create_order/OrderSection";
import ShipmentStatusSection from "./shipment-status/components/update_shipment_status/ShipmentStatusSection";
import ProfileSection from "./profile/components/profile/ProfileSection";
import PharmacyProfileSection from "./pharmacy-profile/components/pharmacy_profile/PharmacyProfileSection";
import CreateAndSendCarePartnerInvitationSection
  from "./create-and-send-care-partner-invitation/components/create_and_send_care_partner_invitation/CreateAndSendCarePartnerInvitationSection";
import AcceptCarePartnerInvitationSection
  from "./accept-care-partner-invitation/components/accept_care_partner_invitation/AcceptCarePartnerInvitationSection";

// Single source of truth: page key -> content component. The key doubles as the URL hash fragment
// (#create-order) and sidebar href, and PageType derives from it, so keys, routing, and rendering
// can't drift. Add a page here and the rest follows.
const PAGES = {
  "add-prescription": AddPrescriptionV2,
  "add-prescription-newrx": AddPrescriptionNewRx,
  "add-insurance-plan": InsurancePlanSection,
  "update-insurance": UpdateInsuranceSection,
  "create-subscription": CreateSubscriptionSection,
  "update-identity-validation-status": UpdateIdentityValidationStatusSection,
  "create-order": OrderSection,
  "update-shipment-status": ShipmentStatusSection,
  "add-profile": ProfileSection,
  "update-pharmacy-profile": PharmacyProfileSection,
  "create-and-send-care-partner-invitation": CreateAndSendCarePartnerInvitationSection,
  "accept-care-partner-invitation": AcceptCarePartnerInvitationSection,
} as const;
type PageType = keyof typeof PAGES;

interface State {
  activePage: PageType;
  navOpen: boolean;
}

class UpdatePharmacyCustomerPage extends React.Component<any, State> {
  private readonly router = new HashPageRouter<PageType>(Object.keys(PAGES) as PageType[], "add-prescription-newrx");

  constructor(props: any) {
    super(props);
    this.state = { activePage: this.router.initialPage(), navOpen: true };
  }

  componentDidMount() {
    this.router.start(() => this.state.activePage, (activePage) => this.setState({ activePage }));
  }

  componentWillUnmount() {
    this.router.stop();
  }

  renderContent = () => {
    const Page = PAGES[this.state.activePage];
    return <Page />;
  };

  render() {
    return (
        <PageShell
            navigationOpen={this.state.navOpen}
            onNavigationChange={(open) => this.setState ({navOpen: open})}
            navigation={
              <SideNavigation
                  header={{
                    text: "Update Pharmacy Customer",
                    href: "#add-prescription-newrx",
                  }}
                  activeHref={`#${this.state.activePage}`}
                  onFollow={(e) => {
                    e.preventDefault ();
                    const activePage = this.router.resolve (e.detail.href.replace ("#", ""));
                    this.setState ({activePage}, () => this.router.navigate (activePage));
                  }}
                  items={[
                    {
                      type: "section",
                      text: "Prescriptions & Orders",
                      items: [
                        {type: "link", text: "Add Prescription via NewRx", href: "#add-prescription-newrx"},
                        {type: "link", text: "Add Prescription via PPCore", href: "#add-prescription"},
                        {type: "link", text: "Create Order", href: "#create-order"},
                        {type: "link", text: "Update Shipment Status", href: "#update-shipment-status"},
                      ],
                    },
                    {
                      type: "section",
                      text: "Insurance",
                      items: [
                        {type: "link", text: "Add Insurance Plan", href: "#add-insurance-plan"},
                        {type: "link", text: "Update Insurance Plan", href: "#update-insurance"},
                      ],
                    },
                    {
                      type: "section",
                      text: "Profile",
                      items: [
                        {type: "link", text: "Add a Pharmacy Profile", href: "#add-profile"},
                        {type: "link", text: "Update Pharmacy Profile Status", href: "#update-pharmacy-profile"},
                        {
                          type: "link",
                          text: "Update Identity Validation Status",
                          href: "#update-identity-validation-status"
                        },
                      ],
                    },
                    {
                      type: "section",
                      text: "Subscriptions",
                      items: [
                        {type: "link", text: "Create Subscription in Arrow Program", href: "#create-subscription"},
                      ],
                    },
                    {
                      type: "section",
                      text: "Care Partners",
                      items: [
                        {
                          type: "link",
                          text: "Create and Send Invitation",
                          href: "#create-and-send-care-partner-invitation"
                        },
                        {type: "link", text: "Accept Invitation", href: "#accept-care-partner-invitation"},
                      ],
                    },
                  ]}
              />
            }
            content={
              <div>
                <Box padding={{top: "l", bottom: "l"}}>
                  <Header variant="h1">Update Pharmacy Customer</Header>
                </Box>
                {this.renderContent ()}
              </div>
            }
        />
    );
  }
}

export default UpdatePharmacyCustomerPage;
